// Liest eine Google-Tabelle im Arristix-Format: Blöcke mit Kopfzeile «Deadline | Video | Stand | …».
// Wird im Browser (Live-Tabelle) und auf dem Server (Automatik) verwendet.
const DAY = 864e5;

export interface GCell {
  formattedValue?: unknown;
  effectiveValue?: { numberValue?: number };
  chipRuns?: { chip?: { richLinkProperties?: { uri?: string } } }[];
  hyperlink?: string;
  dataValidation?: { condition?: { values?: { userEnteredValue?: string }[] } };
}
export interface GSpreadsheet {
  properties?: { title?: string };
  sheets?: { properties?: { title?: string; sheetId?: number }; data?: { rowData?: ({ values?: GCell[] } | null)[] }[] }[];
}

export interface LiveItem {
  key: string;
  tab: string;
  section: string;
  row: number;
  due: string | null;
  dueText: string;
  video: string;
  stand: string;
  files: string;
  filesLabel: string;
  videoUrl: string;
  videoLabel: string;
  todo: string;
  standCol: number;
  todoCol: number;
  standOpts: string[];
  todoOpts: string[];
}
export interface LiveTab {
  title: string;
  gid?: number;
  grid: { t: string; l: string }[][];
}

export const GS_FIELDS = [
  "properties.title",
  "sheets.properties.title",
  "sheets.properties.sheetId",
  "sheets.data.rowData.values.formattedValue",
  "sheets.data.rowData.values.effectiveValue.numberValue",
  "sheets.data.rowData.values.chipRuns.chip.richLinkProperties.uri",
  "sheets.data.rowData.values.hyperlink",
  "sheets.data.rowData.values.dataValidation.condition.values.userEnteredValue",
];

export const colLetter = (i: number) => {
  let s = "";
  i++;
  while (i > 0) {
    const m = (i - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    i = Math.floor((i - 1) / 26);
  }
  return s;
};
export const sheetIdFrom = (u: string) => String(u || "").match(/\/spreadsheets\/d\/([A-Za-z0-9_-]{20,})/)?.[1] ?? null;

const dvOpts = (c: GCell | null | undefined) =>
  (c?.dataValidation?.condition?.values ?? []).map((v) => v.userEnteredValue).filter((v): v is string => !!v);
const cellText = (c: GCell | null | undefined) => (c && c.formattedValue != null ? String(c.formattedValue).trim() : "");
const cellLink = (c: GCell | null | undefined) => {
  if (!c) return "";
  const chip = (c.chipRuns ?? []).map((r) => r?.chip?.richLinkProperties?.uri).find(Boolean);
  const u = chip ?? c.hyperlink ?? "";
  return /^https?:\/\//i.test(u) ? u : "";
};

const MON_DE: Record<string, number> = { jan: 0, feb: 1, mär: 2, mrz: 2, mar: 2, apr: 3, mai: 4, may: 4, jun: 5, jul: 6, aug: 7, sep: 8, okt: 9, oct: 9, nov: 10, dez: 11, dec: 11 };
function serialToIso(n: number) {
  const d = new Date(Date.UTC(1899, 11, 30) + Math.floor(n) * DAY);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 17).toISOString();
}
function parseDeText(s: string): string | null {
  s = String(s || "").trim();
  let m = s.match(/^(\d{1,2})\.\s?(\d{1,2})\.(\d{2,4})?/);
  if (m) {
    let y = m[3] ? +m[3] : new Date().getFullYear();
    if (y < 100) y += 2000;
    return new Date(y, +m[2]! - 1, +m[1]!, 17).toISOString();
  }
  m = s.match(/^(\d{1,2})\.\s?([A-Za-zÄÖÜäöü]{3})[A-Za-zäöü]*\.?\s?(\d{4})?/);
  const mon = m ? MON_DE[m[2]!.toLowerCase()] : undefined;
  if (m && mon != null) return new Date(m[3] ? +m[3] : new Date().getFullYear(), mon, +m[1]!, 17).toISOString();
  return null;
}

export function parseSheet(p: GSpreadsheet): { items: LiveItem[]; tabs: LiveTab[]; title: string } {
  const items: LiveItem[] = [];
  const tabs: LiveTab[] = [];
  for (const sh of p.sheets ?? []) {
    const props = sh.properties ?? {};
    const tab = props.title ?? "";
    const grid: GCell[][] = (sh.data?.[0]?.rowData ?? []).map((r) => r?.values ?? []);
    tabs.push({ title: tab, ...(props.sheetId != null ? { gid: props.sheetId } : {}), grid: grid.map((r) => r.map((c) => ({ t: cellText(c), l: cellLink(c) }))) });
    let section = "";
    let cols: Record<string, number> | null = null;
    let todoCol = -1;
    grid.forEach((r, i) => {
      const low = r.map((c) => cellText(c).toLowerCase());
      if (low.includes("deadline") && low.includes("video")) {
        const c: Record<string, number> = {};
        low.forEach((h, j) => {
          if (h) c[h] = j;
        });
        cols = c;
        const prev = (grid[i - 1] ?? []).map(cellText);
        section = prev[0] || section;
        const pi = prev.findIndex((x) => x.toLowerCase() === "to do");
        todoCol = c["to do"] ?? pi;
        return;
      }
      if (!cols) return;
      const cc: Record<string, number> = cols;
      const g = (k: string) => (cc[k] != null ? r[cc[k]] : null);
      const video = cellText(g("video"));
      if (!video) return;
      const dl = g("deadline");
      const num = dl?.effectiveValue?.numberValue;
      items.push({
        key: `${tab}!${i + 1}`,
        tab,
        section,
        row: i + 1,
        due: num ? serialToIso(num) : parseDeText(cellText(dl)),
        dueText: cellText(dl),
        video,
        stand: cellText(g("stand")),
        files: cellLink(g("link files")),
        filesLabel: cellText(g("link files")),
        videoUrl: cellLink(g("link video")),
        videoLabel: cellText(g("link video")),
        todo: todoCol >= 0 ? cellText(r[todoCol]) : "",
        standCol: cc["stand"] ?? -1,
        todoCol,
        standOpts: dvOpts(g("stand")),
        todoOpts: todoCol >= 0 ? dvOpts(r[todoCol]) : [],
      });
    });
  }
  return { items, tabs, title: p.properties?.title ?? "" };
}

export const isDone = (it: LiveItem) => /^(final|fertig|erledigt|geliefert|done)\b/i.test(it.stand);
/** Offen für die Person mit diesem Vornamen (oder für niemand Bestimmtes). */
export const isMineFor = (first: string) => (it: LiveItem) => !it.todo || (!!first && it.todo.toLowerCase().startsWith(first.toLowerCase()));

/** Wiedererkennung eines Videos über Abschnitt und Titel (gleich in Browser und Server). */
export const itemKey = (it: Pick<LiveItem, "section" | "video">) => `${it.section}|${it.video}`.toLowerCase();
const readable = (x: string) => x.replace(/_+/g, " ").replace(/\s+/g, " ").trim();
/** Projektname für MILKEE, lesbar für Kunden auf der Rechnung: «Arbopark – Challenge Valoarena». */
export const projectName = (it: Pick<LiveItem, "section" | "video">) =>
  [it.section, it.video]
    .map(readable)
    .filter(Boolean)
    .join(" – ");
/** früherer Name («Arbopark_Challenge_Valoarena»), um bestehende Projekte zu erkennen und umzubenennen */
export const legacyProjectName = (it: Pick<LiveItem, "section" | "video">) =>
  [it.section, it.video]
    .map((x) => x.trim().replace(/\s+/g, "_"))
    .filter(Boolean)
    .join("_");
/** Leistung auf der Rechnung: MILKEE druckt «Projekt: Beschreibung» */
export const VIDEO_SERVICE = "Postproduction";

/** Kommentar des Kunden zum Video in Google Drive (mit Antworten) */
export interface VideoComment {
  id: string;
  author: string;
  text: string;
  at: string;
  /** in Drive als erledigt markiert */
  resolved: boolean;
  /** von David im Tool abgehakt (nur intern, der Kunde sieht das nicht) */
  done?: boolean;
  /** Datei, falls der Link auf einen Ordner zeigt */
  file: string;
  replies: { id: string; author: string; text: string; at: string }[];
}
/** Kommentare zu einem Video der Live-Tabelle (Spalte «Link Video») */
export interface VideoFeedback {
  url: string;
  comments: VideoComment[];
  checkedAt: string;
  /** not_shared: Dienstkonto sieht die Datei nicht; not_found; google_error */
  error?: string;
}
/** Offene (nicht erledigte) Kommentare */
export const openComments = (f: VideoFeedback | undefined) => (f?.comments ?? []).filter((c) => !c.resolved && !c.done);
